import { z } from "zod";
import { projectUpdateSchema } from "./project-update";

export enum WorkstreamDeliverableIcon {
    "hire",
    "analysis",
    "iteration",
    "implementation",
    "report",
    "document",
    "strategy",
    "roadmap",
    "training",
    "communication",
    "audit",
    "charter",
    "design",
}

export const workstreamDeliverableSchema = z.object({
    id: z.string(),
    name: z.string(),
    icon: z
        .string()
        .describe("should be one of the predefined icons, but frontend must expect this to be some unknown value"),
    description: z.string(),
    startDate: z.string().date().optional(),
    targetDate: z.string().date().optional(),
    ownerId: z.string().nullable().optional(),
});

export const projectWorkstreamSchema = z.object({
    id: z.string(),
    name: z.string(),
    description: z.string(),
    deliverables: z.array(workstreamDeliverableSchema),
    ownerId: z.string().nullable().optional(),
});

export const projectOutlineSchema = z.object({
    workstreams: z.array(projectWorkstreamSchema),
});

export const taskStatusSchema = z.enum(["done", "started", "on_hold"]);
export type TaskStatus = z.infer<typeof taskStatusSchema>;

export const projectTaskSchema = z.object({
    id: z.string(),
    title: z.string(),
    description: z.string(),
    status: taskStatusSchema.nullable(),
    assignee: z.string().optional().nullable(),
    execution: z.discriminatedUnion("targetActor", [
        z.object({
            targetActor: z.literal("human"),
            suggestedAssignee: z.string().optional(),
        }),
        z.object({
            targetActor: z.literal("bot"),
            type: z.enum(["schedule_meeting", "draft_document", "do_research"]),
        }),
    ]),
    workstreamId: z.string(),
    deliverableId: z.string().nullable(),
});

export const httpProjectSchema = z.object({
    id: z.string(),
    name: z.string(),
    outline: projectOutlineSchema,
    objectives: z.array(z.string()).optional(), // optional for backward compatibility, todo make non-optional
    tasks: z.array(projectTaskSchema),
    confidential: z.boolean().optional(),
    collaborators: z.array(z.string()).nullable().optional(),
    creatorId: z.string(),
    createdAt: z.string(),
    updates: z.array(projectUpdateSchema).optional(), // optional for backward compatibility
});

export type Workstream = z.infer<typeof projectWorkstreamSchema>;
export type Deliverable = z.infer<typeof workstreamDeliverableSchema>;
export type ProjectOutline = z.infer<typeof projectOutlineSchema>;
export type ProjectTask = z.infer<typeof projectTaskSchema>;

export type HttpProject = z.infer<typeof httpProjectSchema>;

export const projectDraftSchema = z.object({
    title: z.string().optional(),
    objectives: z.array(z.string()).optional(),
    workstreams: z.array(projectWorkstreamSchema).optional(),
});

export type ProjectDraft = z.infer<typeof projectDraftSchema>;

export const fileMetadataSchema = z.object({
    id: z.string(),
    name: z.string(),
});

export const httpMessageSchema = z.object({
    createdAt: z.string(),
    role: z.enum(["user", "assistant"]),
    content: z.string(),
    files: z.array(fileMetadataSchema),
});

export type FileMetadata = z.infer<typeof fileMetadataSchema>;
export type HttpMessage = z.infer<typeof httpMessageSchema>;
